import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  Settings,
  BarChart2,
  Users,
  Megaphone,
  Loader2,
  AlertTriangle,
  Save,
  Check,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

type SettingsData = {
  players_see_own_stats_only: boolean;
  stats_visible_to_coaches_only: boolean;
  players_can_see_members: boolean;
  players_can_post: boolean;
};

const DEFAULTS: SettingsData = {
  players_see_own_stats_only: false,
  stats_visible_to_coaches_only: false,
  players_can_see_members: true,
  players_can_post: false,
};

// ─── Toggle ──────────────────────────────────────────────────────────────────

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
        checked ? "bg-indigo-600 dark:bg-indigo-500" : "bg-gray-200 dark:bg-gray-700"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
}

// ─── Setting row ─────────────────────────────────────────────────────────────

function SettingRow({
  icon,
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="flex items-start gap-3 min-w-0">
        <div className="mt-0.5 text-gray-400 dark:text-gray-500 shrink-0">{icon}</div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{label}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</div>
        </div>
      </div>
      <div className="shrink-0 pt-0.5">
        <Toggle checked={checked} onChange={onChange} disabled={disabled} />
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function TeamSettings() {
  const { clubId, teamId } = useParams<{ clubId: string; teamId: string }>();

  const [settings, setSettings] = useState<SettingsData>(DEFAULTS);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!teamId || !clubId) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Utilisateur non connecté.");

        const [settingsRes, teamRoleRes, clubRoleRes] = await Promise.all([
          supabase
            .from("team_settings")
            .select("players_see_own_stats_only, stats_visible_to_coaches_only, players_can_see_members, players_can_post")
            .eq("team_id", teamId)
            .maybeSingle(),
          supabase
            .from("team_members")
            .select("role")
            .eq("team_id", teamId)
            .eq("user_id", user.id)
            .in("status", ["active"])
            .maybeSingle(),
          supabase
            .from("club_members")
            .select("role")
            .eq("club_id", clubId)
            .eq("user_id", user.id)
            .maybeSingle(),
        ]);

        if (!cancelled) {
          if (settingsRes.data) {
            setSettings({
              players_see_own_stats_only: settingsRes.data.players_see_own_stats_only ?? false,
              stats_visible_to_coaches_only: settingsRes.data.stats_visible_to_coaches_only ?? false,
              players_can_see_members: settingsRes.data.players_can_see_members ?? true,
              players_can_post: settingsRes.data.players_can_post ?? false,
            });
          }

          const teamRole = teamRoleRes.data?.role ?? null;
          const clubRole = clubRoleRes.data?.role ?? null;
          const canManage =
            ["owner", "coach", "assistant"].includes(teamRole ?? "") ||
            ["dirigeant", "cadre_technique"].includes(clubRole ?? "");
          setCanEdit(canManage);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Impossible de charger les paramètres.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [teamId, clubId]);

  async function handleSave() {
    if (!teamId) return;
    setSaving(true);
    setSaved(false);
    setError("");

    const { error: upsertErr } = await supabase
      .from("team_settings")
      .upsert({ team_id: teamId, ...settings }, { onConflict: "team_id" });

    if (upsertErr) {
      setError(upsertErr.message);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    setSaving(false);
  }

  function set<K extends keyof SettingsData>(key: K, value: SettingsData[K]) {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  }

  if (loading) {
    return (
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 p-6">
        <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des paramètres…
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="text-center sm:text-left">
        <div className="text-sm text-gray-500 dark:text-gray-400">Équipe</div>
        <div className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center justify-center gap-2 sm:justify-start">
          <Settings className="h-5 w-5 text-gray-500" />
          Paramètres de l'équipe
        </div>
        {!canEdit && (
          <div className="mt-2 text-sm text-amber-600 dark:text-amber-400">
            Vous n'avez pas les droits pour modifier ces paramètres.
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-300 inline-flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Stats section */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-5">
        <div className="pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Statistiques
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          <SettingRow
            icon={<BarChart2 className="h-4 w-4" />}
            label="Statistiques réservées aux coachs"
            description="Seuls les coachs et les dirigeants peuvent accéder aux statistiques de l'équipe. Les joueurs n'y ont pas accès."
            checked={settings.stats_visible_to_coaches_only}
            onChange={(v) => set("stats_visible_to_coaches_only", v)}
            disabled={!canEdit}
          />
          <SettingRow
            icon={<BarChart2 className="h-4 w-4" />}
            label="Stats en mode privé"
            description="Les joueurs ne peuvent consulter que leurs propres statistiques, pas celles de leurs coéquipiers. Ignoré si l'option ci-dessus est activée."
            checked={settings.players_see_own_stats_only}
            onChange={(v) => set("players_see_own_stats_only", v)}
            disabled={!canEdit || settings.stats_visible_to_coaches_only}
          />
        </div>
      </div>

      {/* Members section */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-5">
        <div className="pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Membres
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          <SettingRow
            icon={<Users className="h-4 w-4" />}
            label="Liste des membres visible par les joueurs"
            description="Les joueurs peuvent voir la liste complète des membres de l'équipe."
            checked={settings.players_can_see_members}
            onChange={(v) => set("players_can_see_members", v)}
            disabled={!canEdit}
          />
        </div>
      </div>

      {/* Feed section */}
      <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-5">
        <div className="pt-4 pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Fil d'actualité
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          <SettingRow
            icon={<Megaphone className="h-4 w-4" />}
            label="Les joueurs peuvent publier"
            description="Autorise les joueurs à poster des messages dans le fil d'actualité de l'équipe."
            checked={settings.players_can_post}
            onChange={(v) => set("players_can_post", v)}
            disabled={!canEdit}
          />
        </div>
      </div>

      {/* Save */}
      {canEdit && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-black dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 disabled:opacity-50 transition"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <Check className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? "Enregistrement…" : saved ? "Enregistré !" : "Enregistrer"}
          </button>
          {saved && (
            <span className="text-sm text-green-600 dark:text-green-400">
              Paramètres mis à jour.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
